import { isEmpty, isNil, map } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { Component } from 'react';
import { connect } from 'react-redux';
import { IconName, Stack, withBreakpointRange } from '@optimus-web/core';
import { isMobileOrTablet, Loader, WithBreakpointRange } from '@mews-commander/ui';
import { ActionType, TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { getResolveRoute } from '@mews-commander/legacy-app-instance';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import SubjectField from '../containers/SubjectField';
import { canChangeCurrentThreadName } from '../selectors/threads';
import { getReservationDetailUrl, showReservationDetail } from '../../routes';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { ReservationTab } from '../../../types/reservationTab';
import { PanelBodyElement, PanelCard } from './style';
import ThreadSubject from './ThreadSubject';
import MessageItem from './MessageItem';

interface StateProps {
    canEditName: boolean;
    reservationDetailUrl: string | null;
}

interface DispatchProps {
    showReservation: () => void;
}

export interface MessagesPanelStateProps {
    messages: $TSFixMe[]; // TODO: pt.instanceOf(Message)
    thread: $TSFixMe; // TODO: pt.instanceOf(Thread)
    subjectDraft: string;
    isEditMode: boolean;
    isLoading?: boolean;
}

export interface MessagesPanelDispatchProps {
    onCloseThread: () => void;
    onOpenMembers: () => void;
    onEditClicked: () => void;
    onEditStopped: () => void;
    onMessageResend: (thread: $TSFixMe, message: $TSFixMe) => void;
    onSave: (threadId: $TSFixMe) => void;
}

type OwnProps = WithBreakpointRange & MessagesPanelDispatchProps & MessagesPanelStateProps;

type MessagePanelComponentProps = OwnProps & StateProps & DispatchProps;

class MessagePanelComponent extends Component<MessagePanelComponentProps> {
    wrapper: $TSFixMe;

    componentDidUpdate(prevProps: MessagePanelComponentProps) {
        if (prevProps.messages !== this.props.messages) {
            this.scrollToBottom();
        }
    }

    scrollToBottom = () => {
        if (!isNil(this.wrapper)) {
            this.wrapper.scrollTop = this.wrapper.scrollHeight;
        }
    };

    createLinkAction = ({ actionId, text, icon, url, method }: $TSFixMe): HeadingAction => ({
        type: ActionType.Link,
        actionId,
        text,
        icon,
        url,
        onClick: method,
    });

    handleMessageResend = (message: $TSFixMe) => {
        this.props.onMessageResend(this.props.thread, message);
    };

    handleSave = () => {
        const { thread, subjectDraft } = this.props;
        if (!isEmpty(subjectDraft)) {
            this.props.onSave(thread.id);
        }
    };

    wrapperRef = (c: $TSFixMe) => {
        this.wrapper = c;
        this.scrollToBottom();
    };

    showReservation = () => (this.props as $TSFixMe).showReservation();

    renderLoader = () => (
        <Stack alignment="center" fill>
            <Loader />
        </Stack>
    );

    renderMessages = map((message) => (
        <MessageItem
            key={(message as $TSFixMe).id}
            message={message as $TSFixMe}
            profileId={(message as $TSFixMe).creatorProfileId}
            threadId={this.props.thread.id}
            onResend={this.handleMessageResend}
        />
    ));

    renderMessagesContent = () => (
        <PanelBodyElement ref={this.wrapperRef} data-test-message-thread={this.props.thread.id}>
            {this.renderMessages(this.props.messages)}
        </PanelBodyElement>
    );

    render() {
        const {
            thread,
            messages,
            onCloseThread,
            isEditMode,
            onEditStopped,
            onEditClicked,
            onOpenMembers,
            isLoading,
            breakpointRange,
            canEditName,
            reservationDetailUrl,
        } = this.props;

        const { creatorProfileId, serviceOrderId, subject } = thread;

        let closeAction = null;
        const actions: HeadingAction[] = [];

        if (isEditMode) {
            closeAction = this.createLinkAction({
                actionId: 'close',
                text: <LocalizedText textKey={TextKey.Close} />,
                icon: IconName.CrossClose,
                method: onEditStopped,
            });
            actions.push(
                this.createLinkAction({
                    actionId: 'save',
                    text: <LocalizedText textKey={TextKey.Save} />,
                    icon: IconName.Done,
                    method: this.handleSave,
                })
            );
        } else if (canEditName) {
            actions.push(
                this.createLinkAction({
                    actionId: 'editSubject',
                    text: <LocalizedText textKey={TextKey.EditSubject} />,
                    icon: IconName.Edit,
                    method: onEditClicked,
                })
            );
        }

        if (isMobileOrTablet(breakpointRange)) {
            closeAction = this.createLinkAction({
                actionId: 'back',
                text: <LocalizedText textKey={TextKey.Back} />,
                icon: IconName.ArrowLeft,
                method: onCloseThread,
            });
            actions.push(
                this.createLinkAction({
                    actionId: 'members',
                    text: <LocalizedText textKey={TextKey.Members} />,
                    icon: IconName.CustomerProfiles,
                    method: onOpenMembers,
                })
            );
        }

        if (serviceOrderId) {
            actions.push(
                this.createLinkAction({
                    actionId: 'reservation',
                    text: <LocalizedText textKey={TextKey.Reservation} />,
                    icon: IconName.CustomLink,
                    url: reservationDetailUrl,
                    method: this.showReservation,
                })
            );
        }

        return (
            <PanelCard padding="0">
                <ModalHeader
                    title={<ThreadSubject subject={subject} profileId={creatorProfileId} />}
                    primaryAction={closeAction}
                    actions={actions}
                >
                    <SubjectField onSave={this.handleSave} />
                </ModalHeader>
                {isLoading && isEmpty(messages) ? this.renderLoader() : this.renderMessagesContent()}
            </PanelCard>
        );
    }
}

export default pipe(
    MessagePanelComponent,
    withBreakpointRange,
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state, { thread: { serviceOrderId } }) => ({
            canEditName: canChangeCurrentThreadName(state),
            reservationDetailUrl: serviceOrderId
                ? getReservationDetailUrl(getResolveRoute())({ reservationId: serviceOrderId })
                : null,
        }),
        (dispatch: CommanderThunkDispatch, { thread: { serviceOrderId } }) => ({
            showReservation: () =>
                dispatch(
                    showReservationDetail({ reservationId: serviceOrderId, reservationTab: ReservationTab.Status })
                ),
        })
    )
);
